// Flat inline SVG illustrations for the product families — the compact
// category icon for the /quotes/new product-class picker grid.
//
// The configurator's right-rail spec summary uses the richer, dimensional
// ProjectMockup instead (which also composites the live cover for booklets);
// the two are an intentional split — a small selection icon vs. a product
// visualization — and both read off familyLabel / FamilyCode below.
//
// Single color (currentColor) so a parent's color/opacity drives styling.

import type { JSX } from 'react';

// Canonical product_type values (packages/shared/src/vocabulary.ts) —
// the same string flows through the customer form, the line.config
// payload, and the Nexus pricing catalog. `fulfillment` is a quote-level order
// service and never appears here; `assembly` is an item-grain product type (the
// kit IS the thing being bought), the one assembly flow (ADR-020).
export type FamilyCode =
  | 'single_sheet'
  | 'labels'
  | 'saddle_stitch_book'
  | 'perfect_bound_book'
  | 'hard_bound_book'
  | 'coil_or_wire_o_book'
  | 'loose_leaf'
  | 'media_replication'
  | 'custom_packaging'
  | 'assembly'
  | 'something_else';

// A glyph key that is NOT a product family. The product picker carries a preset
// card alongside the real product_type options, and a preset can be finer-
// grained than the family it rides on: a dust jacket prices as a single sheet,
// yet needs its own drawing to be pickable. So the drawing set is a SUPERSET of
// the families, and every FamilyCode remains a valid key.
export type GlyphCode = FamilyCode | 'kit_box' | 'dust_jacket';

export function FamilyGlyph({ family }: { family: GlyphCode | null | undefined }): JSX.Element {
  switch (family) {
    case 'kit_box':              return <KitBoxGlyph />;
    case 'dust_jacket':          return <DustJacketGlyph />;
    case 'single_sheet':         return <SheetGlyph />;
    case 'labels':               return <LabelsGlyph />;
    case 'saddle_stitch_book':   return <SaddleStitchGlyph />;
    case 'perfect_bound_book':   return <PerfectBoundGlyph />;
    case 'hard_bound_book':      return <HardBoundGlyph />;
    case 'coil_or_wire_o_book':  return <CoilGlyph />;
    case 'loose_leaf':           return <LooseLeafGlyph />;
    case 'media_replication':    return <MediaGlyph />;
    case 'custom_packaging':     return <ShippingBoxGlyph />;
    // The `assembly` family and the `kit_box` glyph its tile is seeded with
    // share the one carton drawing: a kit is a kit.
    case 'assembly':             return <KitBoxGlyph />;
    case 'something_else':       return <SomethingElseGlyph />;
    // null/undefined (no product chosen yet) or an unknown code served by
    // a newer schema falls back to the neutral sheet glyph rather than
    // rendering nothing.
    default:                     return <SheetGlyph />;
  }
}

export function familyLabel(family: FamilyCode | null | undefined): string {
  switch (family) {
    case 'single_sheet':         return 'Flat / Sheet';
    case 'labels':               return 'Labels & Stickers';
    case 'saddle_stitch_book':   return 'Saddle-stitched booklet';
    case 'perfect_bound_book':   return 'Paperback book';
    case 'hard_bound_book':      return 'Hard-bound book';
    case 'coil_or_wire_o_book':  return 'Coil / Wire-O book';
    case 'loose_leaf':           return 'Loose-leaf';
    case 'media_replication':    return 'USB or CD/DVD';
    case 'custom_packaging':     return 'Custom Packaging';
    case 'assembly':             return 'Assembly';
    case 'something_else':       return 'Something else';
    default:                     return 'No product selected yet';
  }
}

function SheetGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      <rect x="18" y="10" width="44" height="60" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="24" y1="22" x2="56" y2="22" stroke="currentColor" strokeWidth="2" />
      <line x1="24" y1="32" x2="56" y2="32" stroke="currentColor" strokeWidth="2" />
      <line x1="24" y1="42" x2="48" y2="42" stroke="currentColor" strokeWidth="2" />
      <line x1="24" y1="52" x2="52" y2="52" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function LabelsGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      <rect x="12" y="14" width="56" height="52" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={16 + col * 17}
            y={18 + row * 16}
            width="14"
            height="12"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        ))
      )}
    </svg>
  );
}

function KitBoxGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      {/* Gathered parts standing in the carton — a sheet and a booklet, drawn
          open-bottomed so neither crosses the box's front face (every drawing in
          this set is an outline, so an overlap would show through). */}
      <path d="M25 40 L25 18 L39 18 L39 40" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M42 40 L42 25 L55 25 L55 40" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <line x1="46" y1="25" x2="46" y2="40" stroke="currentColor" strokeWidth="1.5" />
      {/* Carton: rim, then the front face */}
      <line x1="16" y1="40" x2="64" y2="40" stroke="currentColor" strokeWidth="2" />
      <path d="M16 40 L16 66 L64 66 L64 40" fill="none" stroke="currentColor" strokeWidth="2" />
      {/* Lids swung open at the rim — what makes it an OPEN box being filled */}
      <path d="M16 40 L8 35 L8 31 L16 36 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M64 40 L72 35 L72 31 L64 36 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function DustJacketGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      {/* The jacket laid flat: back panel | spine | front panel, with a tuck-in
          flap folded back at each end. Drawn wide and landscape so it never
          reads as the portrait sheet glyph it prices as. */}
      <rect x="18" y="20" width="44" height="40" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M18 24 L9 27 L9 53 L18 56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M62 24 L71 27 L71 53 L62 56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      {/* Spine panel folds */}
      <line x1="36" y1="20" x2="36" y2="60" stroke="currentColor" strokeWidth="1.5" />
      <line x1="44" y1="20" x2="44" y2="60" stroke="currentColor" strokeWidth="1.5" />
      {/* Title on the front panel (the right-hand one — a wrap runs back to front) */}
      <line x1="48" y1="32" x2="58" y2="32" stroke="currentColor" strokeWidth="1.5" />
      <line x1="48" y1="40" x2="55" y2="40" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function SomethingElseGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      {/* A rounded badge rather than a rectangle: every other drawing in the set
          is a piece of paper, so a squared-off outline would read as one more
          printed thing instead of "none of these". */}
      <rect x="16" y="16" width="48" height="48" rx="8" fill="none" stroke="currentColor" strokeWidth="2" />
      {/* Question-mark hook and stem */}
      <path
        d="M32 34 C32 28 35 25 40 25 C45 25 48 28 48 33 C48 38 40 39 40 45"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="40" cy="53" r="2" fill="currentColor" />
    </svg>
  );
}

function MediaGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      {/* One tile for both carriers: a disc above, a drive below, kept apart
          because every drawing in this set is an outline. */}
      <circle cx="40" cy="28" r="20" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="40" cy="28" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
      {/* Drive body, cap seam, then the connector and its two contacts */}
      <rect x="14" y="54" width="38" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="26" y1="54" x2="26" y2="70" stroke="currentColor" strokeWidth="2" />
      <rect x="52" y="57" width="12" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="56" y1="60" x2="56" y2="64" stroke="currentColor" strokeWidth="1.5" />
      <line x1="60" y1="60" x2="60" y2="64" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function ShippingBoxGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      {/* A CLOSED carton seen from above one corner, so it never reads as the
          open, filled kit carton Assembly uses. Front face, side, then lid. */}
      <path d="M12 29 L50 29 L50 63 L12 63 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M50 29 L68 17 L68 51 L50 63" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 29 L30 17 L68 17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      {/* Tape across the lid seam and down the front */}
      <line x1="31" y1="29" x2="49" y2="17" stroke="currentColor" strokeWidth="1.5" />
      <line x1="31" y1="29" x2="31" y2="39" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function SaddleStitchGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      <path d="M14 18 L40 14 L66 18 L66 64 L40 60 L14 64 Z" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="40" y1="14" x2="40" y2="60" stroke="currentColor" strokeWidth="2" />
      <line x1="38" y1="22" x2="42" y2="22" stroke="currentColor" strokeWidth="3" />
      <line x1="38" y1="40" x2="42" y2="40" stroke="currentColor" strokeWidth="3" />
      <line x1="38" y1="52" x2="42" y2="52" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}

function PerfectBoundGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      <rect x="14" y="14" width="52" height="52" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="22" y1="14" x2="22" y2="66" stroke="currentColor" strokeWidth="2" />
      <line x1="30" y1="24" x2="58" y2="24" stroke="currentColor" strokeWidth="1.5" />
      <line x1="30" y1="34" x2="58" y2="34" stroke="currentColor" strokeWidth="1.5" />
      <line x1="30" y1="44" x2="50" y2="44" stroke="currentColor" strokeWidth="1.5" />
      <line x1="30" y1="54" x2="56" y2="54" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function HardBoundGlyph(): JSX.Element {
  // A rigid, case-bound book: outer case boards (slightly oversized, offset)
  // wrapping an inner book block, with a thick double-line spine to read as a
  // stiff hardback — visually distinct from the single-spine paperback glyph.
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      {/* Case boards — the rigid cover, a touch larger than the block */}
      <rect x="14" y="12" width="54" height="56" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      {/* Inner book block, inset within the case */}
      <rect x="22" y="17" width="42" height="46" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
      {/* Thick case spine (double line) */}
      <line x1="22" y1="12" x2="22" y2="68" stroke="currentColor" strokeWidth="2" />
      <line x1="18" y1="14" x2="18" y2="66" stroke="currentColor" strokeWidth="2" />
      {/* Title lines embossed on the cover */}
      <line x1="30" y1="28" x2="58" y2="28" stroke="currentColor" strokeWidth="1.5" />
      <line x1="30" y1="38" x2="50" y2="38" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function CoilGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      <rect x="22" y="14" width="44" height="52" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
      {[20, 28, 36, 44, 52, 60].map((y) => (
        <circle key={y} cx="20" cy={y} r="3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      ))}
      <line x1="30" y1="26" x2="58" y2="26" stroke="currentColor" strokeWidth="1.5" />
      <line x1="30" y1="38" x2="58" y2="38" stroke="currentColor" strokeWidth="1.5" />
      <line x1="30" y1="50" x2="54" y2="50" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function LooseLeafGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      <rect x="20" y="14" width="44" height="52" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="26" cy="22" r="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="26" cy="40" r="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="26" cy="58" r="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <line x1="34" y1="24" x2="58" y2="24" stroke="currentColor" strokeWidth="1.5" />
      <line x1="34" y1="36" x2="58" y2="36" stroke="currentColor" strokeWidth="1.5" />
      <line x1="34" y1="48" x2="54" y2="48" stroke="currentColor" strokeWidth="1.5" />
      <line x1="34" y1="58" x2="58" y2="58" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
